import React, { useContext } from "react";
import MuPb from "../../../widgets/MuPb";
import {
  Box,
  List,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  ListItemButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import BackBtn from "../../../widgets/BackBtn";
import useApiEp from "../../../hooks/useApiEp";
import { format } from "date-fns"; // Make sure to import this for the timestamp
import { formatRole, getDisplayName } from "../../chatApp/ChatApp";

const MyChatList = ({ internal = false }) => {
  const navigate = useNavigate();
  const { user, loading } = useContext(UserContext);
  const { data, isLoading, isError, error } = useApiEp(`/user/get-my-chats`);

  const handleItemClick = (chat) => {
    navigate(`/${user?.role}/chat?tid=${encodeURIComponent(chat._id)}`, {
      replace: true,
    });
  };

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return <Typography color="error">{error.message}</Typography>;
  }

  return (
    <Box
      ml={"auto"}
      mr={"auto"}
      pl={internal ? 0 : 2}
      pr={internal ? 0 : 2}
      mt={internal ? 0 : 2}
      pt={internal ? 0 : 2}
      maxWidth={720}
      bgcolor={internal ? "transparent" : "white"}
      sx={
        !internal
          ? { borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }
          : {}
      }
    >
      {!internal && (
        <Box display="flex" alignItems="center" mb={2}>
          <BackBtn />
          <Typography
            variant="h6"
            sx={{ ml: 1, fontWeight: 600, color: "#1e293b" }}
          >
            My Chats
          </Typography>
        </Box>
      )}

      <List sx={{ pt: 0 }}>
        {data?.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "#94a3b8", mt: 4, mb: 4 }}
          >
            No conversations yet.
          </Typography>
        ) : (
          data.map((chat, index) => (
            <React.Fragment key={chat._id}>
              <ListItemButton
                alignItems="flex-start"
                onClick={() => handleItemClick(chat)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  p: 1.5,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={getDisplayName(chat)}
                    src={chat.profile_image}
                    sx={{
                      width: 48,
                      height: 48,
                      mr: 1,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  />
                </ListItemAvatar>
                <ListItemText
                  disableTypography
                  primary={
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={0.5}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          lineHeight: 1.2,
                        }}
                      >
                        {getDisplayName(chat)}
                      </Typography>
                      {chat.createdOn && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#94a3b8", whiteSpace: "nowrap", ml: 1 }}
                        >
                          {format(new Date(chat.createdOn), "MMM d")}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box display="flex" flexDirection="column">
                      {chat.role && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#10b981",
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: "0.7rem",
                          }}
                        >
                          {formatRole(chat.role)}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748b",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {chat.latestMessage}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
              {index < data.length - 1 && (
                <Divider
                  component="li"
                  sx={{ borderColor: "#f1f5f9", mx: 2 }}
                />
              )}
            </React.Fragment>
          ))
        )}
      </List>
    </Box>
  );
};

export default MyChatList;
